// score-thresholds-allow-file: numbers here are px render sizes and the 0-100 axis, not bands
import { RadialProgress, Skeleton, Text, TextColor, TextSize } from '@vizit/atoms';
import { ScoreRange } from '@vizit/constants';
import { ArrowNarrowDownIcon, ArrowNarrowUpIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { getScoreRange, serializeScore } from '@vizit/utils';
import * as React from 'react';

interface ScoreRadialProgressProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadialProgress>,
    'barClassName' | 'fillClassName' | 'isIndeterminate' | 'maxValue' | 'value'
  > {
  /**
   * Whether the score is currently loading
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * The vizit score to display
   *
   * @required
   * @type number
   * @minimum 0
   * @maximum 100
   */
  score?: number | null;
  /**
   * The optional start label of the score radial progress
   *
   * @optional
   * @type string
   */
  startLabel?: string;
  /**
   * The end label of the score radial progress
   *
   * @optional
   * @type string
   */
  endLabel?: string;
  /**
   * The inner label of the score radial progress
   *
   * @optional
   * @type string
   */
  innerLabel?: string;
  /**
   * The score difference to display (positive = green, negative = red, 0/null/undefined = hidden)
   *
   * @optional
   * @type number
   */
  scoreDiff?: number | null;
}

const classes = {
  score: cva({
    base: 'text-center',
    variants: {
      size: {
        default: 'text-sm font-semibold',
        32: 'text-sm font-semibold',
        40: 'text-md font-semibold',
        80: 'text-display-xs font-semibold',
      },
    },
  }),
  progress: cva({
    base: '',
    variants: {
      range: {
        [ScoreRange.NotApplicable]: 'stroke-score-not-applicable',
        [ScoreRange.VeryLow]: 'stroke-score-very-low',
        [ScoreRange.Low]: 'stroke-score-low',
        [ScoreRange.Moderate]: 'stroke-score-moderate',
        [ScoreRange.High]: 'stroke-score-high',
        [ScoreRange.VeryHigh]: 'stroke-score-very-high',
        [ScoreRange.Unscored]: 'stroke-score-not-applicable',
      },
    },
  }),
};

function ScoreRadialProgress(props: ScoreRadialProgressProps) {
  const { isLoading, score, size = 32, startLabel, endLabel, innerLabel, scoreDiff: scoreDiffProp, ...other } = props;

  const scoreLabel = React.useMemo(() => serializeScore(score), [score]);

  const shouldShowDiff = React.useMemo(
    () => size === 80 && !isLoading && !!scoreDiffProp,
    [isLoading, scoreDiffProp, size],
  );
  const isPositiveDiff = React.useMemo(() => !!scoreDiffProp && scoreDiffProp > 0, [scoreDiffProp]);
  const scoreDiff = React.useMemo(() => Math.abs(scoreDiffProp ?? 0), [scoreDiffProp]);

  return (
    <div className="flex flex-col items-center justify-center gap-2">
      {startLabel && (
        <Text id="score-radial-progress-label" elementType="span" size={TextSize.Xs} color={TextColor.Tertiary}>
          {startLabel}
        </Text>
      )}
      <RadialProgress
        aria-label={!startLabel && !innerLabel && !endLabel ? 'Vizit score' : undefined}
        aria-labelledby={startLabel || innerLabel || endLabel ? 'score-radial-progress-label' : undefined}
        barClassName="text-slate-shade-01 opacity-25"
        fillClassName={classes.progress({ range: getScoreRange(score) })}
        maxValue={100}
        size={size}
        strokeWidth={3}
        value={score ?? 0}
        style={{ strokeLinecap: 'round' }}
        {...other}
      >
        <div
          className={cx(
            'absolute left-1/2 -translate-x-1/2 flex flex-col items-center justify-center',
            shouldShowDiff ? 'top-[calc(50%+8px)] -translate-y-1/2 gap-0' : 'top-1/2 -translate-y-1/2 gap-2',
          )}
        >
          {isLoading && <Skeleton className="h-8 w-16" variant="square" />}
          {!isLoading && innerLabel && (
            <Text id="score-radial-progress-label" elementType="span" size={TextSize.Xs} color={TextColor.Tertiary}>
              {innerLabel}
            </Text>
          )}
          {!isLoading && (
            <Text
              elementType="span"
              className={cx(classes.score({ size: size as 32 | 40 | 80 }))} // subset of radial progress sizes used for score
            >
              {scoreLabel}
            </Text>
          )}
          {shouldShowDiff && (
            <div
              aria-label={`Score ${isPositiveDiff ? 'increased' : 'decreased'} by ${scoreDiff}.`}
              className={cx('flex items-center gap-1', isPositiveDiff ? 'text-success-600' : 'text-error-600')}
            >
              {isPositiveDiff ? (
                <ArrowNarrowUpIcon aria-hidden="true" size={16} />
              ) : (
                <ArrowNarrowDownIcon aria-hidden="true" size={16} />
              )}
              <Text elementType="span" size={TextSize.Sm} className="text-inherit">
                {scoreDiff}
              </Text>
            </div>
          )}
        </div>
      </RadialProgress>
      {endLabel && (
        <Text id="score-radial-progress-label" elementType="span" size={TextSize.Xs} color={TextColor.Tertiary}>
          {endLabel}
        </Text>
      )}
    </div>
  );
}

export { ScoreRadialProgress };
